import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Star, FileText, MessageCircle, Calendar, CreditCard, ArrowRight, Zap, Clock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency, fmtPaid } from "../contexts/CurrencyContext";
import axios from "axios";
import PaywallModal from "../components/PaywallModal";
import NotificationOptIn from "../components/NotificationOptIn";
import { SmartOfferBanner, useOfferStage } from "../components/PromoBanners";
import KundliPdfActions from "../components/KundliPdfActions";
import TodayHoroscopeCard from "../components/TodayHoroscopeCard";
import InviteFriend from "../components/InviteFriend";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STAT_THEMES = [
  { key: "reports",       label: "Kundli Reports", icon: FileText,      color: "#FFD166", grad: "linear-gradient(135deg, rgba(255,209,102,0.22), rgba(255,209,102,0.04))" },
  { key: "messages",      label: "AI Messages",    icon: MessageCircle, color: "#c084fc", grad: "linear-gradient(135deg, rgba(157,78,221,0.22), rgba(157,78,221,0.04))" },
  { key: "consultations", label: "Consultations",  icon: Calendar,      color: "#86efac", grad: "linear-gradient(135deg, rgba(34,197,94,0.22), rgba(34,197,94,0.04))" },
];

function ColorStat({ theme, value }) {
  const Icon = theme.icon;
  return (
    <div className="dash-stat" style={{ background: theme.grad }}>
      <div className="icon-blob" style={{ background: `${theme.color}22`, color: theme.color }}>
        <Icon size={18} />
      </div>
      <div className="stat-val" style={{ color: theme.color }}>{value || 0}</div>
      <div className="stat-label">{theme.label}</div>
    </div>
  );
}

function ActiveChatBanner({ chat }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const expiresMs = chat.expires_at ? new Date(chat.expires_at).getTime() - now : null;
  const waiting = !chat.astrologer_first_reply_ts;
  const totalSec = expiresMs ? Math.max(0, Math.floor(expiresMs / 1000)) : null;
  const mm = totalSec !== null ? String(Math.floor(totalSec / 60)).padStart(2, "0") : null;
  const ss = totalSec !== null ? String(totalSec % 60).padStart(2, "0") : null;
  return (
    <div data-testid="active-chat-banner" style={{
      marginTop: "12px",
      background: "linear-gradient(135deg, rgba(34,197,94,0.16), rgba(34,197,94,0.03))",
      border: "1px solid rgba(34,197,94,0.3)",
      borderRadius: "14px",
      padding: "14px 18px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: "12px", flexWrap: "wrap",
    }}>
      <div>
        <p style={{ color: "#86efac", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
          <MessageCircle size={16} /> Live consultation in progress
        </p>
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem", marginTop: "2px" }}>
          {waiting
            ? "Waiting for your astrologer to join. You can send messages now — your 30-minute window starts when they reply."
            : (<>Session ends in <strong style={{ color: totalSec < 5 * 60 ? "#fca5a5" : "#FFD166", fontFamily: "ui-monospace, monospace" }}>{mm}:{ss}</strong> — chat freely, no extra payment.</>)
          }
        </p>
      </div>
      <Link to={`/livechat/${chat.chat_id}`} data-testid="resume-chat-btn" className="btn-gold" style={{ padding: "9px 18px", fontSize: "0.84rem", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <Clock size={14} /> Continue Chat
      </Link>
    </div>
  );
}

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const { price } = useCurrency();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [payNote, setPayNote] = useState("");

  // Cashfree sends the customer back here after a browser/UPI-app payment
  // (return_url = /dashboard?order_id=...&plan=...). PayPal comes back with
  // ?paypal_order=<our order id>. Confirm either one and activate.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("order_id");
    const paypalRef = params.get("paypal_order");
    if (!orderId && !paypalRef) return;
    (async () => {
      try {
        const url = paypalRef
          ? `${API}/payments/paypal/capture-by-ref/${paypalRef}`
          : `${API}/payments/verify/${orderId}`;
        const res = await axios.post(url, {}, { withCredentials: true });
        if (res.data.status === "success") {
          await refreshUser();
          setPayNote("Payment received — your plan is active. Enjoy your reading!");
        } else {
          setPayNote("We could not confirm that payment yet. If you paid, it will reflect shortly.");
        }
      } catch {
        setPayNote("We could not confirm that payment yet. If you paid, it will reflect shortly.");
      }
      window.history.replaceState({}, "", "/dashboard");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios.get(`${API}/user/dashboard`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isPremium = user && ["ai_chat", "consultation", "admin"].includes(user.plan);
  const offerStage = useOfferStage();

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#120021", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="cosmic-spinner" />
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "80px" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "600px", height: "600px", background: "radial-gradient(circle, rgba(91,30,140,0.15) 0%, transparent 70%)", top: 0, right: 0, borderRadius: "50%" }} />
      </div>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 16px 80px", position: "relative", zIndex: 1 }}>
        <NotificationOptIn userRole={user?.role} />
        {payNote && (
          <div data-testid="payment-return-note" style={{ marginBottom: "18px", background: "rgba(255,209,102,0.1)", border: "1px solid rgba(255,209,102,0.3)", borderRadius: "12px", padding: "12px 16px", color: "#FFD166", fontSize: "0.86rem" }}>
            {payNote}
          </div>
        )}
        {/* Header */}
        <div className="animate-fadeInUp" style={{ marginBottom: "26px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "linear-gradient(135deg, #9D4EDD, #5B1E8C)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", fontWeight: "700" }}>
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div>
              <h1 className="font-cinzel" style={{ fontSize: "clamp(1.2rem, 3.6vw, 1.6rem)", color: "#FFD166" }}>Welcome, {user?.name?.split(" ")[0]}</h1>
              <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>{user?.email}</p>
            </div>
          </div>

          {!isPremium ? (
            <div style={{ marginTop: "14px", background: "linear-gradient(135deg, rgba(255,209,102,0.12), rgba(255,209,102,0.03))", border: "1px solid rgba(255,209,102,0.2)", borderRadius: "14px", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
              <div>
                <p style={{ color: "rgba(255,255,255,0.78)", fontSize: "0.9rem" }}>You&apos;re on the <strong style={{ color: "#FFD166" }}>Free Plan</strong></p>
                <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem" }}>
                  {user?.bonus_questions > 0
                    ? `${user.bonus_questions} earned free question${user.bonus_questions > 1 ? "s" : ""} ready to use.`
                    : user?.free_question_used ? "Free question used. Upgrade, or invite a friend to earn another." : "1 free AI question remaining."}
                </p>
              </div>
              <button data-testid="upgrade-plan-btn" onClick={() => setShowPaywall(true)} className="btn-gold" style={{ padding: "9px 18px", fontSize: "0.84rem" }}>
                <Zap size={14} style={{ display: "inline", marginRight: "6px" }} />Upgrade — {price("ai_chat")}
              </button>
            </div>
          ) : (
            <div style={{ marginTop: "14px", background: "linear-gradient(135deg, rgba(157,78,221,0.18), rgba(157,78,221,0.03))", border: "1px solid rgba(157,78,221,0.25)", borderRadius: "14px", padding: "14px 18px" }}>
              <p style={{ color: "#c084fc", fontWeight: "600" }}>✦ {user?.plan === "ai_chat" ? "AI Chat Plan Active" : user?.plan === "consultation" ? "Consultation Plan Active" : "Admin Access"}</p>
              {user?.plan_expires_at && <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", marginTop: "4px" }}>Expires: {new Date(user.plan_expires_at).toLocaleString()}</p>}
            </div>
          )}

          {/* Active live chat session banner */}
          {data?.active_chat && (
            <ActiveChatBanner chat={data.active_chat} />
          )}

          {/* Stage-aware next-step offer: free → ₹51 → ₹249 */}
          {offerStage > 0 && !data?.active_chat && (
            <div style={{ marginTop: "12px" }}>
              <SmartOfferBanner />
            </div>
          )}
        </div>

        {/* Today's horoscope */}
        <TodayHoroscopeCard />

        {/* Share the app, earn free questions */}
        <InviteFriend />

        {/* Stats */}
        <div data-testid="dashboard-stats" className="dash-stats-grid">
          {STAT_THEMES.map(theme => (
            <ColorStat key={theme.key} theme={theme} value={data?.stats?.[theme.key]} />
          ))}
        </div>

        {/* Kundli History */}
        <div className="dash-section" style={{ background: "linear-gradient(135deg, rgba(255,209,102,0.06), rgba(255,209,102,0.015))" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
            <h2 style={{ color: "#FFD166" }}><Star size={15} /> Kundli History {data?.recent_reports?.length > 0 && <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem", marginLeft: "6px", fontWeight: 500 }}>({data.recent_reports.length})</span>}</h2>
            <button onClick={() => navigate("/kundli")} style={{ background: "none", border: "none", cursor: "pointer", color: "#FFD166", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "4px" }}>
              New Report <ArrowRight size={14} />
            </button>
          </div>
          {data?.recent_reports?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "360px", overflowY: "auto", paddingRight: "4px" }}>
              {data.recent_reports.map((r, i) => (
                <div key={r.id} data-testid={`report-item-${i}`} className="row">
                  <div>
                    <div style={{ color: "white", fontWeight: "600", fontSize: "0.92rem" }}>{r.name}</div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.76rem", marginTop: "2px" }}>
                      {r.birth_details?.day}/{r.birth_details?.month}/{r.birth_details?.year} — {r.birth_details?.city || "—"}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.72rem", marginTop: "1px" }}>
                      Generated {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexShrink: 0, alignItems: "center", flexWrap: "wrap" }}>
                    <KundliPdfActions reportId={r.id} name={r.name} compact testPrefix={`report-${i}`} />
                    <button onClick={() => { localStorage.setItem("astrofate_report", JSON.stringify(r)); navigate("/kundli/report"); }}
                      className="btn-ghost" style={{ padding: "6px 14px", fontSize: "0.78rem" }}>View</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "26px 16px" }}>
              <p style={{ color: "rgba(255,255,255,0.45)", marginBottom: "14px", fontSize: "0.92rem" }}>No reports yet</p>
              <button onClick={() => navigate("/kundli")} className="btn-gold" style={{ padding: "9px 22px", fontSize: "0.86rem" }}>Generate Your First Kundli</button>
            </div>
          )}
        </div>

        {/* Consultation History */}
        <div className="dash-section" style={{ background: "linear-gradient(135deg, rgba(134,239,172,0.06), rgba(34,197,94,0.015))" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
            <h2 style={{ color: "#86efac" }}><Calendar size={15} /> Consultation History {data?.recent_consultations?.length > 0 && <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem", marginLeft: "6px", fontWeight: 500 }}>({data.recent_consultations.length})</span>}</h2>
            <button onClick={() => navigate("/consultation")} style={{ background: "none", border: "none", cursor: "pointer", color: "#86efac", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "4px" }}>
              Book New <ArrowRight size={14} />
            </button>
          </div>
          {data?.recent_consultations?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "360px", overflowY: "auto", paddingRight: "4px" }}>
              {data.recent_consultations.map((c, i) => (
                <div key={c.id} data-testid={`consultation-item-${i}`} className="row">
                  <div>
                    <div style={{ color: "white", fontSize: "0.9rem", fontWeight: 600 }}>{c.date} at {c.time_slot}</div>
                    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.76rem" }}>{c.concern?.slice(0, 70)}{c.concern?.length > 70 ? "…" : ""}</div>
                    {c.booking_ref && <div style={{ color: "rgba(255,255,255,0.32)", fontSize: "0.72rem", marginTop: "1px" }}>#{c.booking_ref}</div>}
                  </div>
                  <span style={{
                    background: c.status === "confirmed" ? "rgba(34,197,94,0.18)" : c.status === "completed" ? "rgba(96,165,250,0.18)" : c.status === "cancelled" ? "rgba(239,68,68,0.15)" : "rgba(249,115,22,0.18)",
                    color: c.status === "confirmed" ? "#86efac" : c.status === "completed" ? "#93c5fd" : c.status === "cancelled" ? "#fca5a5" : "#fdba74",
                    padding: "3px 10px", borderRadius: "20px", fontSize: "0.72rem", textTransform: "capitalize", fontWeight: 600
                  }}>
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: "20px", fontSize: "0.9rem" }}>No consultations booked yet</p>
          )}
        </div>

        {/* Payment History */}
        {data?.payments?.length > 0 && (
          <div className="dash-section" style={{ background: "linear-gradient(135deg, rgba(103,232,249,0.06), rgba(96,165,250,0.015))" }}>
            <h2 style={{ color: "#67e8f9" }}><CreditCard size={15} /> Payment History</h2>
            {data.payments.map((p, i) => (
              <div key={p.id} data-testid={`payment-item-${i}`} className="row">
                <div>
                  <div style={{ color: "white", fontSize: "0.9rem", textTransform: "capitalize", fontWeight: 600 }}>{p.plan?.replace("_", " ")}</div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.76rem" }}>{p.created_at?.slice(0, 10)}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ color: "#FFD166", fontWeight: "700" }}>{fmtPaid(p.amount, p.currency)}</span>
                  <span style={{ background: p.status === "success" ? "rgba(34,197,94,0.18)" : "rgba(107,114,128,0.15)", color: p.status === "success" ? "#86efac" : "#9ca3af", padding: "2px 8px", borderRadius: "20px", fontSize: "0.72rem", fontWeight: 600 }}>
                    {p.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} onSuccess={() => refreshUser()} />}
    </div>
  );
}
